import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Order {
  id: number;
  order_number: number;
  buy_time: string;
  amount: string;
  buy_price: string;
  sell_price: string;
  spent: string;
  profit: string;
  currency: string;
  order_type: string;
  strategy: string;
}

interface OrdersData {
  orders: Order[];
  total_orders: number;
  currency: string;
  base_currency: string;
  language: string;
}

interface OrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBot: string;
  user: any;
}

const ORDERS_PER_PAGE = 3;

export const OrdersModal: React.FC<OrdersModalProps> = ({
  isOpen,
  onClose,
  selectedBot,
  user
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language === 'ENG' ? 'ENG' : 'RUS';
  const [ordersData, setOrdersData] = useState<OrdersData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Сбрасываем состояние при каждом открытии модального окна
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(0);
      setError(null);
      fetchOrders();
    }
  }, [isOpen]);

  const fetchOrders = async () => {
    if (!user || !selectedBot) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/get_orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: selectedBot,
          language: 'ru'
        })
      });

      const result = await response.json();

      if (result.success) {
        setOrdersData(result.orders_data);
      } else {
        setError(result.error || 'Ошибка получения ордеров');
      }
    } catch (err) {
      setError('Ошибка соединения с сервером');
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = ordersData ? Math.ceil(ordersData.orders.length / ORDERS_PER_PAGE) : 0;
  const startIndex = currentPage * ORDERS_PER_PAGE;
  const endIndex = startIndex + ORDERS_PER_PAGE;
  const currentOrders = ordersData?.orders.slice(startIndex, endIndex) || [];

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-header border border-border rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="pl-2 pr-1 py-2 border-b border-border">
          <div className="grid grid-cols-7 gap-1">
            <div></div>
            <div className="col-span-5 flex items-center justify-center">
              <h2 className="text-lg font-semibold text-white">
                {t('openOrders')}
              </h2>
            </div>
            <div className="flex justify-center">
              <button
                onClick={onClose}
                className="w-8 h-8 text-sm rounded-md transition-colors border border-border text-textSecondary focus:outline-none focus:ring-0 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3">
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="text-white">Загрузка ордеров...</div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-4">
              <div className="text-textSecondary text-center">
                {error === "Trading bot is unavailable" ? t('tradingBotUnavailable') : error}
              </div>
            </div>
          )}

          {!loading && !error && ordersData && (
            <>
              {ordersData.total_orders === 0 ? (
                <div className="flex items-center justify-center py-4">
                  <div className="text-white text-center">
                    У вас нет открытых ордеров
                  </div>
                </div>
              ) : (
                <>
                  {/* Orders List */}
                  <div className="space-y-2">
                    {currentOrders.map((order) => (
                      <div key={order.id} className="bg-background border border-border rounded-lg p-3">
                        <div className="mb-2">
                          <h3 className="text-white font-semibold text-sm">
                            {order.order_number}. {t('sellOrder')}
                          </h3>
                        </div>
                        
                        <div className="text-sm">
                          <div className="text-white font-medium text-sm">
                            {parseFloat(order.amount).toLocaleString(currentLanguage === 'ENG' ? 'en-US' : 'ru-RU', {minimumFractionDigits: 2, maximumFractionDigits: 2})} {ordersData.base_currency}
                          </div>
                          
                          <div className="text-textSecondary -mt-0.5">
                            - {t('boughtAt')} {order.buy_price} ({order.spent}) {order.currency}
                          </div>
                          
                          <div className="text-textSecondary -mt-0.5">
                            - {t('sellingAt')} {order.sell_price} ({(order as any).received_quote ?? (Number(order.amount) * Number(order.sell_price)).toFixed(8)}) {order.currency}
                          </div>
                          
                          <div className="text-textSecondary -mt-0.5">
                            - {t('plannedProfit')} {order.profit} {order.currency}
                          </div>
                          
                          <div className="text-textSecondary mt-0.5">
                            {order.buy_time}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total Orders */}
                  <div className="mt-2 text-center">
                    <div className="text-white text-sm">
                      {t('totalOpenOrders')}: {ordersData.total_orders}
                    </div>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-2 flex items-center justify-center space-x-2">
                      <button
                        onClick={handlePrevPage}
                        disabled={currentPage === 0}
                        className="w-6 h-6 text-xs rounded-md transition-colors border border-border text-textSecondary focus:outline-none focus:ring-0 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-3 h-3" />
                      </button>
                      
                      <span className="text-white text-sm px-2">
                        {currentPage + 1} / {totalPages}
                      </span>
                      
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages - 1}
                        className="w-6 h-6 text-xs rounded-md transition-colors border border-border text-textSecondary focus:outline-none focus:ring-0 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 